import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { SentryUtils } from "@/plugins/sentry";
import { configService } from "@/services/config.service";
import { authService } from "@/services/auth/auth.service";
import type { AuthUser } from "@/services/auth/auth.interface";
import { createLogger } from "@/services/logger/logger.service";
import { useFingerprint } from "@/composables/useFingerprint";
import { useApiClient } from "@/composables/useApiClient";

const logger = createLogger("AuthStore");

interface User extends AuthUser {
  token: string;
  expiration: number;
  refreshToken?: string;
  refreshExpiration?: number;
}

interface LoginCredentials {
  clerk: string;
  password: string;
  remember_me?: boolean;
}

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref<User | null>(null);
  const isAuthenticated = ref(false);
  const error = ref<string | null>(null);
  const isLoading = ref(false);

  // Computed
  const isSessionValid = computed(() => {
    return (
      isAuthenticated.value &&
      user.value !== null &&
      user.value.expiration > Date.now()
    );
  });

  // Get API base URL from configuration service
  const getApiBaseUrl = () => configService.getApiBaseUrl();

  // Actions
  async function login(credentials: LoginCredentials): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      // Get device fingerprint for tracking
      const fingerprint = useFingerprint();
      const deviceId =
        fingerprint.visitorId.value || (await fingerprint.initFingerprint());
      const deviceInfo = fingerprint.getDeviceInfo();

      // Use API client to capture server timings
      const { apiRequest } = useApiClient();

      // Call TixPro authentication API with device tracking
      const loginUrl = `${getApiBaseUrl()}/v0/auth/login`;
      const { data, response } = await apiRequest(loginUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Device-Id": deviceId,
          "X-Device-Fingerprint": deviceId,
        },
        body: JSON.stringify({
          ...credentials,
          device_id: deviceId,
          device_info: deviceInfo,
        }),
      });

      if (!response.ok) {
        const errorData = data || {};
        throw new Error(errorData.message || "Login failed");
      }

      if (!data.success) {
        throw new Error(data.message || "Authentication failed");
      }

      // Calculate token expiration
      const expirationTime = Date.now() + data.tokens.expires_in * 1000;
      const refreshExpirationTime =
        Date.now() + data.tokens.refresh_expires_in * 1000;

      // Create user object
      const authUser: AuthUser = {
        id: data.user.id,
        clerk: data.user.clerk,
        userName: data.user.user_name,
        domain: data.user.domain,
        workgroup: data.user.workgroup,
      };

      // Store auth data using the service
      await authService.setAuth(authUser, {
        accessToken: data.tokens.access_token,
        refreshToken: data.tokens.refresh_token,
        expiresIn: data.tokens.expires_in,
        refreshExpiresIn: data.tokens.refresh_expires_in,
      });

      // Set remember me preference
      if (credentials.remember_me !== undefined) {
        await authService.setRememberMe(credentials.remember_me);
      }

      // Update local state for reactive UI
      user.value = {
        ...authUser,
        token: data.tokens.access_token,
        expiration: expirationTime,
        refreshToken: data.tokens.refresh_token,
        refreshExpiration: refreshExpirationTime,
      };

      isAuthenticated.value = true;

      // Set user context in Sentry
      SentryUtils.setUser({
        id: data.user.id.toString(),
        username: data.user.clerk,
        segment: data.user.workgroup,
      });

      // Add breadcrumb for successful login with device tracking
      SentryUtils.addBreadcrumb(
        `User ${data.user.clerk} logged in successfully`,
        "auth",
        "info",
        {
          domain: data.user.domain,
          workgroup: data.user.workgroup,
          deviceId: deviceId,
          deviceType: deviceInfo.deviceType,
          deviceOS: deviceInfo.deviceOS,
          deviceBrowser: deviceInfo.deviceBrowser,
        },
      );

      // Log successful login with device info
      logger.info("User logged in successfully", {
        userId: data.user.id,
        clerk: data.user.clerk,
        deviceId: deviceId,
        deviceString: deviceInfo.deviceString,
        deviceCategory: deviceInfo.deviceCategory,
        language: deviceInfo.language,
        screenResolution: deviceInfo.screenResolution,
      });
    } catch (err) {
      let errorMessage = "An unknown error occurred";

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      }

      // Set user-friendly error messages
      if (
        errorMessage.toLowerCase().includes("unauthorized") ||
        errorMessage.toLowerCase().includes("invalid credentials") ||
        errorMessage.toLowerCase().includes("authentication failed")
      ) {
        errorMessage = "Invalid username or password. Please try again.";
      } else if (
        errorMessage.toLowerCase().includes("network") ||
        errorMessage.toLowerCase().includes("fetch")
      ) {
        errorMessage = "Network error. Please try again.";
      } else if (errorMessage.toLowerCase().includes("timeout")) {
        errorMessage = "The request timed out. Please try again.";
      } else if (
        errorMessage.toLowerCase().includes("server error") ||
        errorMessage.toLowerCase().includes("500")
      ) {
        errorMessage =
          "Server error. Please try again later or contact support.";
      }

      error.value = errorMessage;
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      // Call logout API if user is authenticated
      const token = await authService.getAccessToken();
      if (isAuthenticated.value && token) {
        const { apiRequest } = useApiClient();
        await apiRequest(`${getApiBaseUrl()}/v0/auth/logout`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (err) {
      // Log error but don't prevent logout
      logger.warn("Failed to notify server of logout", err);
    } finally {
      // Add breadcrumb for logout
      SentryUtils.addBreadcrumb("User logged out", "auth", "info");

      // Clear user context in Sentry
      SentryUtils.clearUser();

      // Clear auth data
      await authService.clearAuth();

      // Clear state
      user.value = null;
      isAuthenticated.value = false;
      error.value = null;
    }
  }

  async function refreshTokens(): Promise<boolean> {
    const refreshToken = await authService.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const { apiRequest } = useApiClient();
      const { data, response } = await apiRequest(`${getApiBaseUrl()}/v0/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      if (!data.success) {
        return false;
      }

      // Update tokens
      await authService.updateTokens({
        accessToken: data.tokens.access_token,
        refreshToken: data.tokens.refresh_token,
        expiresIn: data.tokens.expires_in,
        refreshExpiresIn: data.tokens.refresh_expires_in,
      });

      // Update local state
      if (user.value) {
        const newExpirationTime = Date.now() + data.tokens.expires_in * 1000;
        const newRefreshExpirationTime =
          Date.now() + data.tokens.refresh_expires_in * 1000;

        user.value.token = data.tokens.access_token;
        user.value.expiration = newExpirationTime;
        user.value.refreshToken = data.tokens.refresh_token;
        user.value.refreshExpiration = newRefreshExpirationTime;
      }

      return true;
    } catch (err) {
      logger.error("Token refresh failed", err);
      return false;
    }
  }

  async function checkTokenValidity(): Promise<void> {
    const token = await authService.getAccessToken();
    const authUser = await authService.getUser();

    if (!token || !authUser) {
      await logout();
      return;
    }

    // Check if we can get valid token (handles expiry check)
    const validToken = await authService.getAccessToken();
    if (!validToken) {
      // Token expired, try refresh
      const refreshSuccess = await refreshTokens();
      if (!refreshSuccess) {
        await logout();
        return;
      }
    }

    // Restore user to state
    const currentToken = await authService.getAccessToken();
    if (currentToken) {
      // For now, we need to reconstruct the user object
      // In the future, we might store minimal expiry info
      user.value = {
        ...authUser,
        token: currentToken,
        expiration: Date.now() + 3600000, // Approximate, will be corrected on refresh
      };
      isAuthenticated.value = true;
    }
  }

  async function initAuth(): Promise<void> {
    await authService.initialize();
    await checkTokenValidity();
  }

  // Listen for token refresh events
  window.addEventListener("auth:token-refresh-needed", () => {
    refreshTokens();
  });

  return {
    // State
    user,
    isAuthenticated,
    error,
    isLoading,

    // Computed
    isSessionValid,

    // Actions
    login,
    logout,
    refreshTokens,
    checkTokenValidity,
    initAuth,
  };
});
